/**
 * Phone Input Component for AmalQ
 * International phone number input with smart validation and country detection
 *
 * UX Principles (2025 Standards - "Reward Early, Punish Late"):
 * - Show success signals (valid format) immediately
 * - Delay error display until user has finished (blur or sufficient input)
 * - Smart country detection from phone prefix (+62 → Indonesia)
 * - Editable country code - type the code and country auto-selects
 * - Modern searchable country dropdown
 *
 * Date: 2025-01-13
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AsYouType, type CountryCode } from 'libphonenumber-js';
import { AlertCircle, Info, ShieldCheck } from 'lucide-react';
import { cn } from '../../../lib/utils';

// Import the modular hooks and utilities
import { usePhoneValidation } from './phone/hooks/usePhoneValidation';
import { useCountryDetection } from './phone/hooks/useCountryDetection';
import { getExamplePhone, getCallingCode, detectCountryFromDigits } from './phone/utils/phoneParser';
import { CountrySelector } from './phone/components/CountrySelector';
import type { PhoneVerificationStatus, PhoneValidationError } from './phone/types';

/**
 * Props interface for PhoneInput component
 */
interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  helperText?: string;
  required?: boolean;
  /**
   * Default country for the phone input
   * If not provided, auto-detects from browser locale/timezone
   */
  defaultCountry?: string;
  /**
   * Whether to auto-detect country from browser locale
   * @default true
   */
  autoDetectCountry?: boolean;
  /**
   * External verification status - controls the verification badge
   * This is separate from format validation
   */
  verificationStatus?: PhoneVerificationStatus;
  onValidationChange?: (isValid: boolean) => void;
  /**
   * Callback when validation error changes (with structured error)
   */
  onValidationError?: (error: PhoneValidationError | null) => void;
  /**
   * Whether to show the format validation indicator (digit counter)
   * @default true
   */
  showFormatIndicator?: boolean;
  className?: string;
  id?: string;
}

/**
 * Phone Input Component
 * Production-grade international phone input with smart validation
 *
 * Key Features:
 * 1. "Reward Early, Punish Late" validation timing
 * 2. Smart country detection from prefix (+62 → Indonesia)
 * 3. Editable country code - type directly and country auto-selects
 * 4. Modern searchable country dropdown
 * 5. Specific error messages (too short, too long, invalid format)
 */
export const PhoneInputComponent: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  error: externalError,
  disabled = false,
  placeholder = 'Enter phone number',
  label,
  helperText,
  required = false,
  defaultCountry,
  autoDetectCountry = true,
  verificationStatus = 'none',
  onValidationChange,
  onValidationError,
  showFormatIndicator = true,
  className,
  id,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Country detection hook
  const {
    country,
    setCountry,
    handleValueChange: detectCountry,
    handleCountryChange: buildNewValue,
  } = useCountryDetection({
    initialValue: value,
    defaultCountry: defaultCountry as CountryCode | undefined,
    autoDetectCountry,
  });

  // Validation hook with timing state machine
  const {
    isValid,
    errorMessage,
    shouldShowError,
    digitInfo,
    handleFocus: onValidationFocus,
    handleBlur: onValidationBlur,
    handleChange: updateValidationTiming,
    resetValidation,
  } = usePhoneValidation({
    value,
    country,
    required,
    externalError,
    onValidationChange,
    onValidationError,
  });

  /**
   * Format phone number as user types
   */
  const formatPhoneNumber = useCallback(
    (input: string, countryCode: CountryCode): string => {
      if (!input) return '';

      try {
        const formatter = new AsYouType(countryCode);
        return formatter.input(input);
      } catch {
        return input;
      }
    },
    []
  );

  /**
   * Handle input changes
   * - Smart detection: if user types digits without +, check if it's a country code first
   * - Detect country from prefix
   * - Format as user types
   * - Update validation timing
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      // Smart handling when value doesn't start with +
      if (newValue && !newValue.startsWith('+')) {
        const digits = newValue.replace(/\D/g, '');

        // Check if the digits could be a country calling code (1-4 digits at start)
        if (digits.length >= 1 && digits.length <= 15) {
          // Try to detect country from the leading digits
          const detectedFromDigits = detectCountryFromDigits(digits);

          if (detectedFromDigits) {
            // User typed a valid country code - switch to that country
            const detectedCallingCode = getCallingCode(detectedFromDigits);
            const remainingDigits = digits.slice(detectedCallingCode.length);
            newValue = `+${detectedCallingCode}${remainingDigits}`;
            // Update country immediately
            setCountry(detectedFromDigits);
          } else {
            // No country code detected - prepend current country's code
            const callingCode = getCallingCode(country);
            if (callingCode) {
              newValue = `+${callingCode}${digits}`;
            }
          }
        }
      }

      // Detect country from the full value (handles +XX format)
      detectCountry(newValue);

      // Format the number
      const formatted = formatPhoneNumber(newValue, country);

      // Update parent
      onChange(formatted || newValue);

      // Update validation timing
      updateValidationTiming(formatted || newValue);
    },
    [country, onChange, detectCountry, formatPhoneNumber, updateValidationTiming, setCountry]
  );

  /**
   * Handle country change from dropdown
   * - Preserves national number
   * - Resets validation timing to avoid immediate error
   */
  const handleCountrySelect = useCallback(
    (newCountry: CountryCode) => {
      // Build new value preserving national number
      const newValue = buildNewValue(newCountry, value);

      // Format the new value
      const formatted = formatPhoneNumber(newValue, newCountry);

      // Update parent
      onChange(formatted || newValue);

      // Reset validation timing so errors don't show immediately
      resetValidation();

      // Update country state
      setCountry(newCountry);

      // Focus the input after selection
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
    [value, onChange, buildNewValue, formatPhoneNumber, resetValidation, setCountry]
  );

  /**
   * Handle focus
   */
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onValidationFocus();

    // If empty, add the country code
    if (!value) {
      const callingCode = getCallingCode(country);
      if (callingCode) {
        onChange(`+${callingCode}`);
      }
    }
  }, [value, country, onChange, onValidationFocus]);

  /**
   * Handle blur
   */
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onValidationBlur();
  }, [onValidationBlur]);

  /**
   * Get example format for current country
   */
  const exampleFormat = useMemo(() => {
    return getExamplePhone(country);
  }, [country]);

  /**
   * Display value - show formatted or placeholder with country code
   */
  const displayValue = useMemo(() => {
    if (value) return value;
    return '';
  }, [value]);

  /**
   * Dynamic placeholder with country code
   */
  const dynamicPlaceholder = useMemo(() => {
    const callingCode = getCallingCode(country);
    if (callingCode) {
      return `+${callingCode} ${placeholder}`;
    }
    return placeholder;
  }, [country, placeholder]);

  /**
   * Determine visual state for styling
   */
  const getDisplayState = () => {
    if (verificationStatus === 'verified') return 'verified';
    if (shouldShowError && (externalError || errorMessage)) return 'error';
    if (value && !digitInfo.isValid && digitInfo.current > 0) return 'warning';
    if (value && isValid) return 'valid';
    return 'default';
  };

  const displayState = getDisplayState();
  const showError = shouldShowError && (externalError || errorMessage);
  const displayedError = externalError || errorMessage;

  return (
    <div className={cn('space-y-1.5', className)}>
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Phone Input Container */}
      <div className="relative">
        <div
          className={cn(
            'relative flex items-center rounded-xl transition-all duration-200',
            'border bg-white dark:bg-gray-800',
            // Focus ring when focused
            isFocused && 'ring-2 ring-offset-0',
            {
              // Default state (not focused)
              'border-gray-200 dark:border-gray-700':
                displayState === 'default' && !isFocused,

              // Focused default OR valid format (emerald border)
              'border-emerald-500 ring-emerald-500/20':
                (displayState === 'default' && isFocused) || displayState === 'valid',

              // Verified state (green with badge)
              'border-green-500 ring-green-500/20': displayState === 'verified',

              // Error state
              'border-red-400 ring-red-500/20': displayState === 'error',

              // Warning state (digit count mismatch)
              'border-amber-400 ring-amber-500/20': displayState === 'warning',

              // Disabled state
              'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 cursor-not-allowed':
                disabled,
            }
          )}
        >
          {/* Country Selector */}
          <div className="shrink-0 border-r border-gray-200 dark:border-gray-700">
            <CountrySelector
              value={country}
              onChange={handleCountrySelect}
              disabled={disabled}
            />
          </div>

          {/* Phone Input */}
          <input
            ref={inputRef}
            id={id}
            type="tel"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={dynamicPlaceholder}
            disabled={disabled}
            autoComplete="tel"
            className={cn(
              'flex-1 px-3 py-2.5 sm:px-4 sm:py-3',
              'text-sm sm:text-base',
              'bg-transparent border-0',
              'text-gray-900 dark:text-gray-100',
              'placeholder:text-gray-400 dark:placeholder:text-gray-500',
              'focus:outline-hidden focus:ring-0',
              disabled && 'cursor-not-allowed opacity-60'
            )}
            aria-label={label || 'Phone number'}
            aria-invalid={showError ? 'true' : 'false'}
            aria-describedby={
              showError ? `${id}-error` : helperText ? `${id}-helper` : undefined
            }
          />

          {/* Status Icons */}
          <div className="absolute right-3 flex items-center pointer-events-none">
            {/* Verified: Phone has been verified with OTP */}
            {verificationStatus === 'verified' && (
              <div className="flex items-center gap-1.5 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-full">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium text-green-700 dark:text-green-400">
                  Verified
                </span>
              </div>
            )}
            {/* Error: Show error icon */}
            {displayState === 'error' && (
              <AlertCircle className="h-5 w-5 text-red-500" />
            )}
            {/* Warning: Digit count mismatch */}
            {displayState === 'warning' && (
              <Info className="h-5 w-5 text-amber-500" />
            )}
          </div>
        </div>

        {/* Helper Text / Error / Digit Counter */}
        <div className="mt-1.5 flex items-start justify-between gap-2">
          <div className="flex-1">
            {showError && displayedError ? (
              <p
                id={`${id}-error`}
                className="text-sm text-red-600 dark:text-red-400"
              >
                {displayedError}
              </p>
            ) : helperText ? (
              <p
                id={`${id}-helper`}
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {helperText}
              </p>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Example: {exampleFormat}
              </p>
            )}
          </div>

          {/* Digit Counter - Only show when format indicator enabled */}
          {showFormatIndicator && value && digitInfo.hasInfo && (
            <div
              className={cn(
                'text-xs font-medium px-2 py-0.5 rounded-full shrink-0',
                {
                  // Valid format - subtle neutral indicator
                  'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400':
                    digitInfo.isValid && isValid,
                  // Invalid digit count - warning indicator
                  'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400':
                    !digitInfo.isValid,
                  // Partial input - still typing
                  'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500':
                    digitInfo.isValid && !isValid,
                }
              )}
            >
              {digitInfo.current}/{digitInfo.expected} digits
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Default export for convenience
 */
export default PhoneInputComponent;
